import { ArrowRight, Minus, Plus } from "lucide-react";

import type { Evidence, Locator } from "@talent-align/engine";

interface Props {
  evidence: Evidence[];
  rationale?: string;
}

function formatLocator(loc: Locator | null | undefined): string {
  if (!loc) return "";
  if (typeof loc.bulletIndex === "number") {
    return `${loc.section} · bullet ${loc.bulletIndex + 1}`;
  }
  return loc.section;
}

export function EvidenceList({ evidence, rationale }: Props) {
  const strengths = evidence.filter((e) => e.kind === "strength");
  const gaps = evidence.filter((e) => e.kind === "gap");

  return (
    <div className="ta-card p-6">
      <h3 className="text-sm uppercase tracking-wider text-slate-500 font-semibold mb-4">
        Evidence
      </h3>

      {rationale && (
        <p className="text-sm text-slate-300 italic leading-relaxed mb-5 pb-5 border-b border-slate-800/60">
          "{rationale}"
        </p>
      )}

      {strengths.length > 0 && (
        <div className="mb-6">
          <div className="flex items-center gap-2 mb-3 text-emerald-400">
            <Plus className="w-4 h-4" />
            <span className="text-xs uppercase tracking-wider font-semibold">
              Strengths ({strengths.length})
            </span>
          </div>
          <ul className="space-y-4">
            {strengths.map((e, idx) => (
              <li
                key={idx}
                className="rounded-lg border border-slate-800/60 bg-slate-900/30 p-3"
              >
                <div className="flex items-start gap-2 text-xs">
                  <div className="flex-1 min-w-0">
                    <div className="text-[10px] uppercase tracking-wider text-slate-600 font-semibold mb-1">
                      JD · {formatLocator(e.jdLocator)}
                    </div>
                    <div className="text-slate-300 leading-snug">
                      {e.jdLocator?.text}
                    </div>
                  </div>
                  <ArrowRight className="w-3 h-3 text-slate-600 mt-4 flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    <div className="text-[10px] uppercase tracking-wider text-slate-600 font-semibold mb-1">
                      Resume · {formatLocator(e.resumeLocator)}
                    </div>
                    <div className="text-slate-200 leading-snug">
                      {e.resumeLocator?.text}
                    </div>
                  </div>
                </div>
                {e.note && (
                  <div className="mt-2 text-xs text-slate-500 italic border-t border-slate-800/60 pt-2">
                    {e.note}
                  </div>
                )}
              </li>
            ))}
          </ul>
        </div>
      )}

      {gaps.length > 0 && (
        <div>
          <div className="flex items-center gap-2 mb-3 text-rose-400">
            <Minus className="w-4 h-4" />
            <span className="text-xs uppercase tracking-wider font-semibold">
              Gaps ({gaps.length})
            </span>
          </div>
          <ul className="space-y-2">
            {gaps.map((e, idx) => (
              <li
                key={idx}
                className="rounded-lg border border-rose-500/10 bg-rose-500/5 p-3 text-xs"
              >
                <div className="text-[10px] uppercase tracking-wider text-rose-500/70 font-semibold mb-1">
                  Missing · {formatLocator(e.jdLocator)}
                </div>
                <div className="text-slate-300 leading-snug">
                  {e.jdLocator?.text}
                </div>
                {e.note && (
                  <div className="mt-1.5 text-slate-500 italic">{e.note}</div>
                )}
              </li>
            ))}
          </ul>
        </div>
      )}

      {strengths.length === 0 && gaps.length === 0 && (
        <div className="text-sm text-slate-500 italic">No evidence extracted.</div>
      )}
    </div>
  );
}
